import { Box, Group, Select, Stack, Table, Text, TextInput } from "@mantine/core"
import { DatePickerInput, DateValue } from "@mantine/dates"
import { EyeIcon, MagnifyingGlassIcon } from "@phosphor-icons/react"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import useSWR from "swr"
import type { CodReconStatus, ICodReconciliation } from "../../apis/cod-reconciliation"
import * as ReconApi from "../../apis/cod-reconciliation"
import { dateFormat } from "../../utils/date"
import { DeliveryRowAction } from "../delivery/action-controls"
import { DotPill, KpiRow, TableFrame, TablePager } from "../delivery/atoms"
import { ListState } from "../ui"
import { CodReconciliationDetailModal } from "./CodReconciliationDetailModal"
import { COD_STATUS_TONE, Money, useDriverNames } from "./cod-shared"
import classes from "./cod.module.css"

const STATUSES: CodReconStatus[] = ["DRAFT", "SETTLED", "DISPUTED", "WRITTEN_OFF"]

const num = (v: unknown): number => Number(v) || 0

function toIsoDate(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0")
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** Бэлэн төлөлт (canvas Ord_DeliveryTabsB): driver-day cash reconciliations. List needs ORD_DN_MNG. */
export function CodReconciliationWorklist() {
  const { t } = useTranslation(["settings", "delivery"])
  const [workDate, setWorkDate] = useState<Date | null>(null)
  const [statusFilter, setStatusFilter] = useState<string>("")
  const [search, setSearch] = useState("")
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const driverName = useDriverNames()

  const isoDate = workDate ? toIsoDate(workDate) : null

  const { data, isLoading, error, mutate } = useSWR(
    ["cod-reconciliation.list", isoDate, statusFilter],
    () =>
      ReconApi.list({
        filter: {
          ...(statusFilter ? { status: statusFilter } : {}),
          ...(isoDate ? { workDateFrom: isoDate, workDateTo: isoDate } : {}),
        },
        offset: { limit: 100, skip: 0 },
      }),
    { revalidateOnFocus: false },
  )

  const rows: ICodReconciliation[] = useMemo(() => data?.rows ?? [], [data?.rows])
  const needle = search.trim().toLowerCase()
  const filteredRows = needle
    ? rows.filter(
        (r) =>
          driverName(r.driverUserId).toLowerCase().includes(needle) ||
          dateFormat(r.workDate).toLowerCase().includes(needle),
      )
    : rows

  const openCount = filteredRows.filter((r) => r.status === "DRAFT").length
  const totalExpected = filteredRows.reduce((s, r) => s + num(r.expectedAmount), 0)
  const totalActual = filteredRows.reduce((s, r) => s + num(r.actualAmount), 0)
  const totalDiscrepancy = filteredRows.reduce((s, r) => s + num(r.discrepancyAmount), 0)
  const filterActive = Boolean(workDate) || statusFilter !== "" || needle !== ""

  const onClear = () => {
    setWorkDate(null)
    setStatusFilter("")
    setSearch("")
  }

  // Filters narrow the list without resetting `page`; clamp so a shorter result never shows an empty page.
  const safePage = Math.min(page, Math.max(1, Math.ceil(filteredRows.length / pageSize)))

  const renderTable = () => {
    if (isLoading) return <ListState state="loading" columns={8} />
    if (error) return <ListState state="error" onRetry={() => mutate()} />
    if (filteredRows.length === 0) {
      return filterActive ? (
        <ListState state="noResults" action={{ onClick: onClear }} />
      ) : (
        <ListState state="empty" title={t("delivery:manage.cod.emptyTitle")} description={t("delivery:manage.cod.emptyText")} />
      )
    }
    return (
      <TableFrame
        footer={
          <TablePager page={safePage} pageSize={pageSize} total={filteredRows.length} onPageChange={setPage} onPageSizeChange={setPageSize} />
        }
      >
        <Table.ScrollContainer minWidth={900}>
          <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={90}>{t("delivery:manage.col.action")}</Table.Th>
                <Table.Th>{t("cod.reconciliation.table.workDate")}</Table.Th>
                <Table.Th>{t("cod.reconciliation.table.driver")}</Table.Th>
                <Table.Th ta="right">{t("cod.reconciliation.table.deliveryCount")}</Table.Th>
                <Table.Th ta="right">{t("cod.reconciliation.table.expected")}</Table.Th>
                <Table.Th ta="right">{t("cod.reconciliation.table.actual")}</Table.Th>
                <Table.Th ta="right">{t("cod.reconciliation.table.discrepancy")}</Table.Th>
                <Table.Th>{t("cod.reconciliation.table.status")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filteredRows.slice((safePage - 1) * pageSize, safePage * pageSize).map((r) => (
                <Table.Tr key={r.id} className={classes.row} onClick={() => setSelectedId(r.id)}>
                  <Table.Td onClick={(e) => e.stopPropagation()}>
                    <DeliveryRowAction
                      label={t("cod.reconciliation.actions.viewDetail")}
                      icon={<EyeIcon />}
                      onClick={() => setSelectedId(r.id)}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Text ff="monospace" fz="var(--nw-fz-mono)">
                      {dateFormat(r.workDate)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text fz="var(--nw-fz-body)">{driverName(r.driverUserId)}</Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text ff="monospace" fz="var(--nw-fz-mono)">
                      {r.deliveryCount ?? 0}
                    </Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text ff="monospace" fz="var(--nw-fz-mono)" className={classes.nowrap}>
                      <Money value={num(r.expectedAmount)} />
                    </Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text ff="monospace" fz="var(--nw-fz-mono)" className={classes.nowrap}>
                      <Money value={num(r.actualAmount)} />
                    </Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text ff="monospace" fz="var(--nw-fz-mono)" fw={num(r.discrepancyAmount) !== 0 ? 650 : 400} className={classes.nowrap}>
                      <Money value={num(r.discrepancyAmount)} />
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <DotPill tone={COD_STATUS_TONE[r.status] ?? "grey"}>
                      {t(`cod.reconciliation.status.${r.status}`, { defaultValue: "-" })}
                    </DotPill>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </TableFrame>
    )
  }

  return (
    <Stack gap="md">
      <KpiRow
        items={[
          {
            key: "open",
            tone: openCount > 0 ? "amber" : "grey",
            label: t("cod.reconciliation.summary.open"),
            value: openCount,
            meta: t("delivery:manage.cod.openMeta"),
          },
          {
            key: "expected",
            label: t("cod.reconciliation.summary.expected"),
            value: <Money value={totalExpected} />,
            meta: t("delivery:manage.cod.expectedMeta"),
          },
          {
            key: "actual",
            label: t("cod.reconciliation.summary.actual"),
            value: <Money value={totalActual} />,
            meta: t("delivery:manage.cod.actualMeta"),
          },
          {
            key: "discrepancy",
            tone: totalDiscrepancy !== 0 ? "red" : "grey",
            label: t("cod.reconciliation.summary.discrepancy"),
            value: <Money value={totalDiscrepancy} />,
            meta: t("delivery:manage.cod.discrepancyMeta"),
          },
        ]}
      />

      <Group gap="md" wrap="wrap" align="flex-end">
        <Select
          label={t("cod.reconciliation.filters.status.label")}
          data={[
            { value: "", label: t("cod.reconciliation.status.ALL") },
            ...STATUSES.map((s) => ({ value: s, label: t(`cod.reconciliation.status.${s}`) })),
          ]}
          value={statusFilter}
          onChange={(v) => setStatusFilter(v ?? "")}
          w={200}
          allowDeselect={false}
        />
        <DatePickerInput
          label={t("cod.reconciliation.filters.workDate.label")}
          value={workDate}
          onChange={(v: DateValue) => setWorkDate(v ? new Date(v as unknown as string) : null)}
          valueFormat="YYYY-MM-DD"
          placeholder={t("cod.reconciliation.allDates")}
          clearable
          w={200}
        />
        <Box flex={1} miw={200}>
          <TextInput
            label={t("cod.reconciliation.filters.search.label")}
            placeholder={t("cod.reconciliation.filters.search.placeholder")}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            leftSection={<MagnifyingGlassIcon size={16} />}
          />
        </Box>
      </Group>

      {renderTable()}

      <CodReconciliationDetailModal
        opened={Boolean(selectedId)}
        reconciliationId={selectedId}
        onClose={() => setSelectedId(null)}
        onActioned={() => {
          mutate()
          setSelectedId(null)
        }}
      />
    </Stack>
  )
}
